import { defineComponent as _defineComponent } from 'vue'
import { computed, unref, useRoute, useRouter, useStore } from "@nuxtjs/composition-api";
import { storeToRefs } from "pinia";

import { useAmplitude, useAuth, useLinkService, useVuetify } from "@/composables";
import { getAmplitudeStore } from "@/store/amplitude";
import { useCreatorShop } from "@/store/creator-shop";
import { useCreatorShopPostsStore } from "@/store/creator-shop-posts";
import { getExperimentsStore } from "@/store/experiments";
import { getLikesStore } from "@/store/likes";
import { getLtksStore } from "@/store/ltks";
import { FEATURE_FLAG_ACTIVE, FLAG_CREATOR_SHOP_SEARCH } from "@/utils/experiments/constants";
import { Actions } from "@/utils/snowplow/enums";
import { createPostImpression } from "@/utils/snowplow/factory";


export default /*#__PURE__*/_defineComponent({
  __name: 'PostFeedItem',
  props: {
  postId: {
    type: String,
    required: true,
  },
  displayName: {
    type: String,
    required: true,
  },
  profileId: {
    type: String,
    required: true,
  },
  position: {
    type: Number,
    required: false,
    default: undefined,
  },
},
  setup(__props) {

const props = __props;



const store = useStore();
const route = useRoute();
const router = useRouter();
const auth = useAuth();
const amplitude = useAmplitude();
const vuetify = useVuetify();
const linkService = useLinkService();
const creatorShopPostsStore = useCreatorShopPostsStore();
const experimentsStore = getExperimentsStore(store);
const ltksStore = getLtksStore(store);
const { posts } = storeToRefs(creatorShopPostsStore);

const isCreatorShopSearchEnabled = computed(
  () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
);
const showLegacyPost = computed(() => !isCreatorShopSearchEnabled.value);
const post = computed(() => posts?.value[props.postId]);
const postSearchResult = computed(() => ltksStore.ltks[props.postId]);

const isVideo = computed(() => {
  const showLegacyPostValue = showLegacyPost.value;
  const legacyMediaType = post.value?.mediaType;
  const videoMediaId = postSearchResult.value?.videoMediaId;
  return showLegacyPostValue ? legacyMediaType === "video" : Boolean(videoMediaId);
});
const isLiked = computed(() => Boolean(getLikesStore(store).likes[props.postId]));
const referringComponent = computed(() => amplitude?.getReferringComponentByPage(route.value.name) ?? "");
const HERO_IMAGE_DEFAULT_WIDTH = 450;
const HERO_IMAGE_HALF_WIDTH = 225;
const optimizedWidth = computed(() => (vuetify.breakpoint.xsOnly ? HERO_IMAGE_HALF_WIDTH : HERO_IMAGE_DEFAULT_WIDTH));

const aspectRatio = computed(() => {
  const showLegacyPostValue = showLegacyPost.value;
  const legacyPostValue = post.value;
  const postValue = postSearchResult.value;
  if (showLegacyPostValue) {
    return legacyPostValue?.heroImageWidth !== undefined && legacyPostValue.heroImageHeight !== undefined
      ? legacyPostValue.heroImageWidth / legacyPostValue.heroImageHeight
      : 1;
  }
  return typeof postValue?.heroImageWidth === "number" && typeof postValue.heroImageHeight === "number"
    ? postValue.heroImageWidth / postValue.heroImageHeight
    : 1;
});

const heroImageLink = computed(() => {
  const showLegacyPostValue = showLegacyPost.value;

  const legacyPostImageLink = post.value?.imageLink;
  const postImageLink = postSearchResult.value?.heroImage;
  if (showLegacyPostValue) {
    return legacyPostImageLink
      ? linkService.getImageLink({
          url: legacyPostImageLink,
          width: unref(optimizedWidth).toString(),
          crop: false,
          useDefaultDpr: false,
        })
      : "";
  }
  return postImageLink
    ? linkService.getImageLink({
        url: postImageLink,
        width: unref(optimizedWidth).toString(),
        crop: false,
        useDefaultDpr: false,
      })
    : "";
});

const heroImageSrcSet = computed(() =>
  heroImageLink.value
    ? `${heroImageLink.value}&dpr=1 1x, ${heroImageLink.value}&dpr=2 2x, ${heroImageLink.value}&dpr=3 3x`
    : "",
);

const calculatedHeight = computed(() => unref(optimizedWidth) * (1 / aspectRatio.value));

const onFavoriteChange = async () => {
  const postId = props.postId;
  const likesStore = getLikesStore(store);
  const creatorShopTab = useCreatorShop().getLastSeenShopTab;

  if (auth.loggedIn) {
    const payload: LtKFavoriteProps = {
      "ltk-id": postId,
      "profile-id": props.profileId,
      "referring-component": referringComponent.value,
      method: "CLICK",
    };

    if (creatorShopTab) {
      payload["creator-shop-tab"] = creatorShopTab;
    }

    if (isLiked.value) {
      await likesStore.deleteLike({ ltkId: postId });
      amplitude?.sendEvent("/unlike", payload);
    } else {
      await likesStore.createLike({ ltkId: postId });
      amplitude?.sendEvent("/like", payload);
    }
  } else {
    localStorage.setItem("favorite-post", postId);
    getLikesStore(store).setLoggedOutFavLtkId(postId);
    getAmplitudeStore(store).setReferringComponent(referringComponent.value);
    router.push({ path: "/signup", query: { "shopltk-signup-prompt": "favorite-post" } });
  }
};

const setReferringTab = () => useCreatorShop().setReferringShopTab();

const postImpressionPayload = computed(() =>
  createPostImpression(Actions.View, {
    id: props.postId,
    creator_id: props.profileId,
    position: props.position,
  }),
);

return { __sfc: true,props, store, route, router, auth, amplitude, vuetify, linkService, creatorShopPostsStore, experimentsStore, ltksStore, posts, isCreatorShopSearchEnabled, showLegacyPost, post, postSearchResult, isVideo, isLiked, referringComponent, HERO_IMAGE_DEFAULT_WIDTH, HERO_IMAGE_HALF_WIDTH, optimizedWidth, aspectRatio, heroImageLink, heroImageSrcSet, calculatedHeight, onFavoriteChange, setReferringTab, postImpressionPayload }
}

})